import React from "react";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import NaturalNextSteps from "@/components/NaturalNextSteps";
import TopImage from "@/components/TopImage";
import BodyText from "@/components/BodyText";
import RichText from "@/components/RichText";
import useContent from "@/hooks/useContent";
import useSeo from "@/hooks/useSeo";
import { IMG } from "@/lib/images";

const CALENDLY = "https://calendly.com/hooverdan56/30min";

export default function ForwardReset() {
  const t = useContent("forward_reset");
  useSeo("forward_reset", {
    title: "Forward Reset — Quantum Results",
    description:
      "A private advisory experience for high-responsibility people navigating significant transitions.",
  });
  return (
    <div data-testid="page-forward-reset">
      <TopImage src={IMG.forwardReset.hero.src} alt={IMG.forwardReset.hero.alt} credit={IMG.forwardReset.hero.credit} testId="forward-reset-top-image" />
      <PageHero
        eyebrow={t("hero_eyebrow", "Forward Reset")}
        title={t("hero_title", <>Sometimes the most valuable next step is a different <span className="text-brand-blue">conversation</span>.</>)}
        lede={t("hero_lede", "A professional advisory experience for people navigating significant change, increasing responsibility, or an important transition.")}
        testId="fr-hero"
      />

      <Section testId="fr-intro">
        <BodyText
          className=""
          value={t.body(
            "intro_body",
            `Forward Reset is a professional advisory experience for people navigating significant change, increasing responsibility, or an important transition.

It isn't about giving you more to do.

It's about creating the space to think more clearly, reconnect with what matters most, and move forward with greater confidence and intention.

**Sometimes one thoughtful conversation changes everything that follows.**`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section eyebrow={t("section2_eyebrow", "You may be here because…")} title={t("section2_title", "You may be here because…")} testId="fr-recognition">
          <BodyText
            className=""
            value={t.body(
              "section2_body",
              `From the outside, life may look successful. Inside, it feels increasingly difficult to think as clearly as you once did.

Perhaps you're carrying more responsibility than ever before. You're navigating a significant transition. You're facing an important decision.

Or you've simply reached a point where you know something needs to change, but you're not yet sure what that change looks like.

**You don't need someone to tell you how to live your life. You need a trusted thinking partner who can help you see more clearly, think more deeply, and move forward with greater confidence.**`
            )}
          />
        </Section>
      </section>

      <Section
        eyebrow={t("section3_eyebrow", "What we do together")}
        title={t("section3_title", "We create conditions for better thinking.")}
        testId="fr-what-we-do"
      >
        <BodyText
          className=""
          value={t.body(
            "section3_body",
            `Every Forward Reset engagement begins with the understanding that your situation is unique. There isn't a formula to follow or a one-size-fits-all process.

Instead, we create a confidential space to slow down, think clearly, explore what matters most, and make thoughtful decisions aligned with where you want to go next.

Sometimes that means untangling complexity.

Sometimes it means recognizing patterns that have quietly shaped your decisions.

Sometimes it means discovering that you already know the next step — you simply needed the space to see it.`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section
          eyebrow={t("section4_eyebrow", "Is Forward Reset right for you?")}
          title={t("section4_title", "Forward Reset may be right for you if…")}
          testId="fr-fit"
        >
          <BodyText
            className=""
            value={t.body(
              "section4_body",
              `It may be a good fit if you're experiencing a significant transition, carrying increasing responsibility, or facing an important decision that deserves thoughtful attention.

You value honest conversation more than quick answers.

You're open to seeing your situation from a different perspective.

And you're ready to move forward intentionally rather than simply reacting to what's in front of you.

**If that describes where you are today, I'd be honored to have a conversation with you. You don't need to have everything figured out before we talk. In fact, many people begin Forward Reset because they don't.**`
            )}
          />
        </Section>
      </section>

      <section className="container-narrow py-24" data-testid="fr-calendly">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] items-start">
          <div className="max-w-lg">
            <RichText as="div" className="eyebrow mb-5" value={t("begin_eyebrow", "Let's begin with a conversation")} />
            <RichText
              as="h2"
              className="display text-[30px] sm:text-[40px] mb-8"
              value={t("begin_title", "Every meaningful change begins somewhere.")}
            />
            <BodyText
              value={t.body(
                "begin_body",
                `If something in these words resonates with you, there's no need to decide everything today.

Let's simply begin with a conversation.

We'll explore where you are, what you're navigating, and whether Forward Reset feels like the right next step.

If it does, we'll move forward together. If it doesn't, I hope our conversation leaves you with greater clarity than when we began.

**Either way, it will have been time well spent.**`
              )}
            />
            <a
              href={CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="fr-calendly-fallback"
              className="mt-8 inline-flex items-center gap-2 text-[14px] text-brand-blue hover:text-brand-ink transition-colors"
            >
              Prefer a direct link? Open Calendly ↗
            </a>
          </div>
          <div className="rounded-lg border border-brand-rule/70 bg-white overflow-hidden shadow-[0_20px_60px_-40px_rgba(26,37,64,0.35)]">
            <iframe
              title="Schedule a Conversation with Dan Hoover"
              src={`${CALENDLY}?hide_gdpr_banner=1&background_color=eef2f7&text_color=1a2540&primary_color=1a4c8e`}
              className="w-full h-[720px]"
              data-testid="fr-calendly-iframe"
            />
          </div>
        </div>
      </section>

      <NaturalNextSteps
        items={[
          { to: "/about", label: "About Dan Hoover" },
          { to: "/four-levels", label: "The Four Levels Experience" },
          { to: "/contact", label: "Contact Quantum Results" },
        ]}
      />
    </div>
  );
}
